import { useCallback, useEffect, useRef, useState } from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddIcon from "@mui/icons-material/Add";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { notifyEmail } from "./EmailNotifications";
import baseUrl from "../../../api/baseUrl";
import { PanelCard, SectionHeader } from "../components/accountUi";

type EmailTemplate = {
  id: string;
  name: string;
  subject: string;
  body: string;
};

type TemplatesData = {
  placeholders: string[];
  defaults: EmailTemplate[];
  custom: EmailTemplate[];
};

type FormState = { id: string | null; name: string; subject: string; body: string };

const EMPTY_FORM: FormState = { id: null, name: "", subject: "", body: "" };

function errorMessageFrom(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

export default function EmailTemplatesPanel() {
  const [data, setData] = useState<TemplatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const lastField = useRef<"subject" | "body">("body");
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const testEmailRef = useRef<HTMLInputElement>(null);

  const loadTemplates = useCallback(() => {
    setLoading(true);
    setError(null);
    return baseUrl
      .get("/client/account/email-templates")
      .then((res) => {
        if (res?.data?.success) setData(res.data.data);
        else setError("Could not load email templates right now.");
      })
      .catch(() => setError("Could not load email templates right now."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openForm = (next: FormState) => {
    setForm(next);
    setFormError(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const insertPlaceholder = (key: string) => {
    if (!form) return;
    const token = `{{${key}}}`;
    const field = lastField.current;
    const input = field === "subject" ? subjectRef.current : bodyRef.current;
    const value = form[field];
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    setForm({ ...form, [field]: value.slice(0, start) + token + value.slice(end) });
    setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const saveTemplate = () => {
    if (!form) return;
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      setFormError("Enter a template name, subject and body.");
      return;
    }
    setSaving(true);
    setFormError(null);
    const payload = { name: form.name, subject: form.subject, body: form.body };
    const request = form.id
      ? baseUrl.put(`/client/account/email-templates/${form.id}`, payload)
      : baseUrl.post("/client/account/email-templates", payload);
    request
      .then(() => {
        setForm(null);
        loadTemplates();
      })
      .catch((err) => setFormError(errorMessageFrom(err, "Could not save the template.")))
      .finally(() => setSaving(false));
  };

  const deleteTemplate = (template: EmailTemplate) => {
    if (!window.confirm(`Delete the template "${template.name}"?`)) return;
    baseUrl
      .delete(`/client/account/email-templates/${template.id}`)
      .then(() => {
        if (form?.id === template.id) setForm(null);
        loadTemplates();
      })
      .catch((err) => setError(errorMessageFrom(err, "Could not delete the template.")));
  };

  const sendTest = (template: EmailTemplate) => {
    if (!testEmail.trim()) {
      notifyEmail("warning", "Email address needed", "Enter an email address in \"Send email to\" first.");
      testEmailRef.current?.focus();
      return;
    }
    setSendingId(template.id);
    setResult(null);
    baseUrl
      .post("/client/account/email-test", { to: testEmail, templateId: template.id })
      .then((res) => {
        const message = res?.data?.message || "Email sent successfully";
        setResult({ ok: true, message });
        notifyEmail("success", "Email sent!", `"${template.name}" was sent to ${testEmail.trim()}.`);
      })
      .catch((err) => {
        const message = errorMessageFrom(err, "Email could not be sent");
        setResult({ ok: false, message });
        notifyEmail("error", "Email not sent", message);
      })
      .finally(() => setSendingId(null));
  };

  const renderCard = (template: EmailTemplate, isDefault: boolean) => (
    <div
      key={template.id}
      className="flex flex-col rounded-2xl border border-slate-200/80 p-4 dark:border-slate-700/80"
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-slate-800 dark:text-white">{template.name}</p>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            isDefault
              ? "bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300"
              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          }`}
        >
          {isDefault ? "Default" : "Custom"}
        </span>
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
        Subject: <span className="font-normal">{template.subject}</span>
      </p>
      <p className="mt-1 line-clamp-5 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">
        {template.body}
      </p>
      <div className="mt-auto flex flex-wrap gap-3 pt-3 text-xs font-semibold">
        {isDefault ? (
          <button
            type="button"
            onClick={() =>
              openForm({
                id: null,
                name: `${template.name} (copy)`,
                subject: template.subject,
                body: template.body,
              })
            }
            className="text-primary hover:underline"
          >
            Use as base
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => openForm({ ...template })}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => deleteTemplate(template)}
              className="text-rose-600 hover:underline dark:text-rose-400"
            >
              Delete
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => sendTest(template)}
          disabled={sendingId !== null}
          className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1 text-primary transition hover:bg-primary hover:text-white hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          <SendOutlinedIcon sx={{ fontSize: 14 }} />
          {sendingId === template.id ? "Sending…" : "Send email"}
        </button>
      </div>
    </div>
  );

  return (
    <PanelCard className="overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <SectionHeader
            icon={DescriptionOutlinedIcon}
            title="Email Templates"
            subtitle="Default templates and your own templates with a subject and body"
          />
          <button
            type="button"
            onClick={() => openForm({ ...EMPTY_FORM })}
            className="inline-flex items-center justify-center gap-2 self-start rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
          >
            <AddIcon sx={{ fontSize: 16 }} />
            Create template
          </button>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {error ? (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
            {error}
          </div>
        ) : null}

        {form ? (
          <div ref={formRef}>
            <PanelCard className="p-4">
              <p className="text-sm font-semibold text-slate-800 dark:text-white">
                {form.id ? "Edit template" : "Create template"}
              </p>
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Template name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Post-treatment care"
                    maxLength={80}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Subject</span>
                  <input
                    ref={subjectRef}
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    onFocus={() => (lastField.current = "subject")}
                    placeholder="e.g. Caring for your teeth after treatment at {{clinic_name}}"
                    maxLength={200}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Body</span>
                  <textarea
                    ref={bodyRef}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    onFocus={() => (lastField.current = "body")}
                    placeholder={"Dear {{patient_name}},\n\nWrite your message here.\n\n{{clinic_name}}"}
                    rows={8}
                    maxLength={10000}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </label>
                <div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Click to insert into the subject or body (where the cursor was last):
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(data?.placeholders ?? []).map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => insertPlaceholder(key)}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 hover:border-primary hover:text-primary dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
                      >
                        {`{{${key}}}`}
                      </button>
                    ))}
                  </div>
                </div>
                {formError ? (
                  <p className="text-xs text-rose-700 dark:text-rose-300">{formError}</p>
                ) : null}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={saveTemplate}
                    disabled={saving}
                    className="rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save template"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(null)}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </PanelCard>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Send email to</p>
          <input
            ref={testEmailRef}
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="name@example.com"
            className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white sm:max-w-sm"
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Placeholders are filled with sample values.
          </p>
        </div>
        {result ? (
          <p
            className={`text-xs ${
              result.ok ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"
            }`}
          >
            {result.message}
          </p>
        ) : null}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Your templates
          </p>
          {loading && !data ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading templates…</p>
          ) : (data?.custom ?? []).length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No templates yet. Click Create template.
            </p>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {data!.custom.map((template) => renderCard(template, false))}
            </div>
          )}
        </div>

      </div>
    </PanelCard>
  );
}
